import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router'
import { Id, TimelineGroupBase } from 'react-calendar-timeline'
import { CaretDown, MagnifyingGlass, Plus } from '@phosphor-icons/react'
import moment from 'moment'
import diacritics from 'diacritics'
import { toast } from '../../utils/toast'
import { useDispatch } from 'react-redux'
import 'react-calendar-timeline/lib/Timeline.css'
import TeamTimeOffCalendar from '../../components/TeamTimeOffCalendar/TeamTimeOffCalendar'
import { GoogleCalendarConsentModal } from '../../components/TimeOffForm/TimeOffForm'
import LogTimeOffModal, {
  TimeOffFormData,
} from '../../components/LogTimeOffModal/LogTimeOffModal'
import DeleteTimeOffModal from '../../components/LogTimeOffModal/DeleteTimeOffModal'
import ModalBambooIntegration from '../../components/ModalBambooIntegration'
import { authContext } from '../../components/CognitoContextProvider/CognitoContextProvider'
import bamboohrLogo from '../../assets/bamboohr_logo.png'
import { TimeOff, UsersTimesOff } from '../../types/TimeOff'
import { User } from '../../types/User'
import { Role } from '../../types/Roles'
import { timeoffApi } from '../../redux/timeoff.api'
import {
  useArchiveTimeOffMutation,
  useCreateTimeOffMutation,
  useLazyGetAllUsersTimesOffQuery,
  useLazyGetGoogleCalendarOAuthStartQuery,
  useUpdateTimeOffMutation,
} from '../../redux/timeoff.endpoints'
import { useLazyGetAllTribesQuery } from '../../redux/tribe.endpoints'
import { useLazyGetAllTeamsQuery } from '../../redux/team.endpoints'
import { useLazyGetAllRolesQuery } from '../../redux/role.endpoints'
import { useLazyGetAllUsersQuery } from '../../redux/user.endpoints'
import { submitTimeOffUpdate } from '../../utils/timeoffUpdate'
import Loader from '../../components/Loader/Loader'

const SEARCH_DEBOUNCE_MS = 500
const FILTER_APPLY_DELAY_MS = 50
// Safety valve: if the main thread never goes idle, reveal anyway.
const IDLE_TIMEOUT_MS = 600

type IdleWindow = Window & {
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout: number },
  ) => number
  cancelIdleCallback?: (handle: number) => void
}

type CalendarFilters = {
  searchQuery: string
  selectedGuildId: number
  selectedTribe: number
  selectedTeam: number
}

const DEFAULT_FILTERS: CalendarFilters = {
  searchQuery: '',
  selectedGuildId: -1,
  selectedTribe: 0,
  selectedTeam: 0,
}

interface Props {
  profile: User
}

const TeamTimeOffView = ({ profile }: Props) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { auth } = useContext(authContext)

  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedGuildId, setSelectedGuildId] = useState(-1)
  const [selectedTribe, setSelectedTribe] = useState(0)
  const [selectedTeam, setSelectedTeam] = useState(0)
  const [appliedFilters, setAppliedFilters] =
    useState<CalendarFilters>(DEFAULT_FILTERS)
  const [isFilterLoading, setIsFilterLoading] = useState(false)
  const [isCalendarReady, setIsCalendarReady] = useState(false)
  const isInitialFilterSync = useRef(true)
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [usersTimesOff, setUsersTimesOff] = useState<UsersTimesOff[]>([])
  const [guilds, setGuilds] = useState<Role[]>([])
  const [bambooModalOpen, setBambooModalOpen] = useState(false)
  const [isEditTimeoffModalOpen, setEditTimeoffModalOpen] = useState(false)
  const [isArchiveTimeOffModalOpen, setIsArchiveTimeOffModalOpen] =
    useState(false)
  const [selectedTimeoff, setSelectedTimeoff] = useState({} as TimeOff)
  const [selectedTimeOffUser, setSelectedTimeOffUser] = useState<User>(
    {} as User,
  )
  const [showLogModal, setShowLogModal] = useState(false)
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false)
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const [pendingLogData, setPendingLogData] = useState<{
    startDate: string
    endDate: string
    description: string
    userId?: number
  } | null>(null)

  const [updateTimeOff, updateTimeOffResult] = useUpdateTimeOffMutation()
  const [archiveTimeOff, archiveTimeOffResult] = useArchiveTimeOffMutation()
  const [createTimeOff] = useCreateTimeOffMutation()
  const [getGoogleCalendarOAuthStart] =
    useLazyGetGoogleCalendarOAuthStartQuery()
  const [getUsersTimesOff, getUsersTimesOffResponse] =
    useLazyGetAllUsersTimesOffQuery()
  const [getAllUsers, getAllUsersResponse] = useLazyGetAllUsersQuery()
  const [getTribes, { data: tribesData, isFetching: isTribesFetching }] =
    useLazyGetAllTribesQuery()
  const [getTeams, { data: teamsData, isFetching: isTeamsFetching }] =
    useLazyGetAllTeamsQuery()
  const [getAllRoles, getAllRolesResponse] = useLazyGetAllRolesQuery()

  const idToken = useMemo(() => {
    if (!auth.data?.signInUserSession?.idToken?.jwtToken) return ''
    return auth.data.signInUserSession.idToken.jwtToken
  }, [auth.data])

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput)
    }, SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timer)
  }, [searchInput])

  // Apply filters on a deferred tick so "Loading..." can paint before the heavy table rebuild.
  useEffect(() => {
    const nextFilters: CalendarFilters = {
      searchQuery,
      selectedGuildId,
      selectedTribe,
      selectedTeam,
    }

    if (isInitialFilterSync.current) {
      isInitialFilterSync.current = false
      setAppliedFilters(nextFilters)
      return
    }

    setIsFilterLoading(true)
    const timeoutId = window.setTimeout(() => {
      setAppliedFilters(nextFilters)
      setIsFilterLoading(false)
    }, FILTER_APPLY_DELAY_MS)

    return () => window.clearTimeout(timeoutId)
  }, [searchQuery, selectedGuildId, selectedTribe, selectedTeam])

  const allTimesOff = useMemo(() => {
    return usersTimesOff
      .map(userTimesOff => userTimesOff.timeoffs)
      .flat()
      .map(timeoff => ({
        approved: timeoff.approved,
        description: timeoff.description,
        endDate: moment(timeoff.endDate).startOf('day').toDate(),
        name: timeoff.name,
        startDate: moment(timeoff.startDate).startOf('day').toDate(),
        userId: timeoff.userId,
        id: timeoff.id,
      }))
  }, [usersTimesOff])

  const usersGroups = useMemo((): TimelineGroupBase[] => {
    const {
      searchQuery: appliedSearchQuery,
      selectedGuildId: appliedGuildId,
      selectedTribe: appliedTribe,
      selectedTeam: appliedTeam,
    } = appliedFilters

    const loweredQuery = diacritics.remove(appliedSearchQuery.toLowerCase())
    const filteredUsersByQuery =
      appliedSearchQuery === ''
        ? allUsers
        : allUsers.filter(user => {
            const firstName = diacritics.remove(user.firstName).toLowerCase()
            const lastName = diacritics.remove(user.lastName).toLowerCase()
            const query = diacritics.remove(loweredQuery).toLowerCase()

            return `${firstName} ${lastName}`.includes(query)
          })

    const filteredUsersByTribe =
      appliedTribe === 0
        ? filteredUsersByQuery
        : filteredUsersByQuery.filter(user =>
            user.teamAllocations.some(
              allocation => allocation.team.tribeId === appliedTribe,
            ),
          )

    const filteredUsersByTeam =
      appliedTeam === 0
        ? filteredUsersByTribe
        : filteredUsersByTribe.filter(user =>
            user.teamAllocations.some(
              allocation => allocation.teamId === appliedTeam,
            ),
          )

    const filteredUsersByGuild =
      appliedGuildId === -1
        ? filteredUsersByTeam
        : filteredUsersByTeam.filter(user => user.guildId === appliedGuildId)

    const today = new Date()

    return filteredUsersByGuild
      .filter(user => {
        if (!user.end_on) return true
        return new Date(user.end_on) >= today
      })
      .slice()
      .sort((a, b) => {
        const fullNameA = `${a.firstName} ${a.lastName}`.toLowerCase()
        const fullNameB = `${b.firstName} ${b.lastName}`.toLowerCase()
        return fullNameA.localeCompare(fullNameB)
      })
      .map(user => ({
        id: user.id,
        title: user.firstName.concat(' ', user.lastName),
        height: 46,
      }))
  }, [allUsers, appliedFilters])

  const activeGuilds = useMemo(() => {
    const today = new Date()
    return guilds
      .filter(guild =>
        allUsers.some(
          user =>
            user.guildId === guild.id &&
            (!user.end_on || new Date(user.end_on) >= today),
        ),
      )
      .slice()
      .sort((a, b) => a.role.localeCompare(b.role))
  }, [guilds, allUsers])

  const sortedTribes = useMemo(() => {
    return tribesData
      ?.map(tribe => ({ ...tribe }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(tribe => {
        const teams = tribe.teams.filter(
          team => !team.endDate || new Date(team.endDate) >= new Date(),
        )
        return teams.length > 0
      })
  }, [tribesData])

  const sortedTeams = useMemo(() => {
    return teamsData
      ?.map(team => ({ ...team }))
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(team => {
        if (selectedTribe === 0 || team.tribeId === selectedTribe) {
          return team.endDate === null || new Date(team.endDate) >= new Date()
        }
        return false
      })
  }, [teamsData, selectedTribe])

  const isLoading =
    isFilterLoading ||
    !getAllUsersResponse.isSuccess ||
    !getUsersTimesOffResponse.isSuccess ||
    !getAllRolesResponse.isSuccess ||
    tribesData === undefined ||
    teamsData === undefined ||
    getAllUsersResponse.isFetching ||
    getUsersTimesOffResponse.isFetching ||
    getAllRolesResponse.isFetching ||
    isTribesFetching ||
    isTeamsFetching ||
    (getAllUsersResponse.data !== undefined &&
      getAllUsersResponse.data.length > 0 &&
      allUsers.length === 0) ||
    (getUsersTimesOffResponse.data !== undefined &&
      getUsersTimesOffResponse.data.length > 0 &&
      usersTimesOff.length === 0)

  const showCalendar = !isLoading && usersGroups.length > 0
  const isPreparingView = isLoading || (showCalendar && !isCalendarReady)
  const controlsTooltip = isPreparingView ? 'Loading…' : undefined

  useEffect(() => {
    if (!showCalendar) {
      setIsCalendarReady(false)
      return
    }

    const markReady = () => setIsCalendarReady(true)
    const idleWindow = window as IdleWindow

    if (idleWindow.requestIdleCallback) {
      const handle = idleWindow.requestIdleCallback(markReady, {
        timeout: IDLE_TIMEOUT_MS,
      })
      return () => idleWindow.cancelIdleCallback?.(handle)
    }

    // Two frames: the commit that laid out the table has been through a frame.
    let secondFrame = 0
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(markReady)
    })

    return () => {
      cancelAnimationFrame(firstFrame)
      cancelAnimationFrame(secondFrame)
    }
  }, [showCalendar])

  const selectItem = useCallback(
    (id: Id) => {
      const timeOff = allTimesOff.find(timeOffItem => timeOffItem.id == id)
      if (timeOff) {
        const user = usersTimesOff.find(
          userTimesOff => userTimesOff.id == timeOff.userId,
        )
        if (user) {
          setSelectedTimeOffUser(user)
        }
        setSelectedTimeoff(timeOff)
        setEditTimeoffModalOpen(true)
      }
    },
    [allTimesOff, usersTimesOff],
  )

  const handleGroupNameClick = useCallback(
    (groupId: Id) => {
      navigate(`/members/${groupId}`)
    },
    [navigate],
  )

  const submitUpdateTimeOff = async (
    data: TimeOffFormData,
  ): Promise<boolean> => {
    const updatedTimeoff = {
      ...selectedTimeoff,
      startDate: moment(data.startDate).toDate(),
      endDate: moment(data.endDate).toDate(),
      description: data.description,
    }
    setSelectedTimeoff(updatedTimeoff)
    return submitTimeOffUpdate({
      selectedTimeoff: updatedTimeoff,
      allTimesOff,
      timeOffOwner: selectedTimeOffUser,
      profile,
      updateTimeOff: args => updateTimeOff(args),
    })
  }

  useEffect(() => {
    getAllRoles()
  }, [])

  useEffect(() => {
    if (getAllRolesResponse?.data) {
      setGuilds(getAllRolesResponse.data)
    }
  }, [getAllRolesResponse])

  useEffect(() => {
    setSelectedTeam(0)
  }, [selectedTribe])

  useEffect(() => {
    if (profile?.company) {
      getTribes({
        companyId: profile.company.id,
        pageSize: 0,
        pageNumber: 0,
        sortBy: 'id',
        searchQuery: '',
        sortType: 'DESC',
      })
      getTeams(profile.company.id)
    }
  }, [profile])

  useEffect(() => {
    getAllUsers({
      companyId: profile.company.id,
      pageSize: 0,
      pageNumber: 0,
      sortBy: 'id',
      searchQuery: '',
      sortType: 'DESC',
      isActive: true,
    })
    getUsersTimesOff(profile.id.toString())
  }, [profile.company.id, profile.id])

  useEffect(() => {
    if (getAllUsersResponse?.data) {
      setAllUsers(getAllUsersResponse.data)
    }
  }, [getAllUsersResponse])

  useEffect(() => {
    if (getUsersTimesOffResponse?.data) {
      setUsersTimesOff(getUsersTimesOffResponse.data)
    }
  }, [getUsersTimesOffResponse])

  useEffect(() => {
    if (updateTimeOffResult?.isSuccess) {
      setUsersTimesOff([
        ...usersTimesOff.filter(
          userTimesOff => userTimesOff.id != updateTimeOffResult.data.id,
        ),
        updateTimeOffResult.data,
      ])
      setEditTimeoffModalOpen(false)
      toast.success('Time Off updated successfully', {
        toastId: 'update-timeoff-success',
      })
    }
  }, [updateTimeOffResult])

  useEffect(() => {
    if (!updateTimeOffResult.isError) return

    const errorData = updateTimeOffResult.error as {
      data?: { error?: string }
    }

    toast.error(
      errorData?.data?.error || 'Unable to update Time Off. Please try again.',
      {
        toastId: 'update-timeoff-error',
      },
    )
  }, [updateTimeOffResult.isError])

  useEffect(() => {
    if (archiveTimeOffResult?.isSuccess) {
      toast.success('Time Off deleted successfully!', {
        toastId: 'archive-timeoff-success',
      })

      setUsersTimesOff(prev =>
        prev.map(user =>
          user.id === selectedTimeOffUser.id
            ? {
                ...user,
                timeoffs: user.timeoffs.filter(
                  timeoff => timeoff.id !== selectedTimeoff.id,
                ),
              }
            : user,
        ),
      )
    }
  }, [archiveTimeOffResult])

  const handleAddTimeOff = () => setShowLogModal(true)

  const waitForGoogleOAuthResult = (popup: Window | null) =>
    new Promise<{ success: boolean; message?: string }>(resolve => {
      const onMessage = (event: MessageEvent) => {
        const payload = event.data
        if (!payload || payload.source !== 'google-calendar-oauth') return
        cleanup()
        resolve({ success: Boolean(payload.success), message: payload.message })
      }
      const cleanup = () => {
        clearInterval(interval)
        clearTimeout(timeout)
        window.removeEventListener('message', onMessage)
      }
      const interval = window.setInterval(() => {
        if (popup && popup.closed) {
          cleanup()
          resolve({
            success: false,
            message: 'Google login window was closed before completing auth.',
          })
        }
      }, 500)
      const timeout = window.setTimeout(() => {
        cleanup()
        resolve({
          success: false,
          message: 'Google login timed out. Please try again.',
        })
      }, 120000)
      window.addEventListener('message', onMessage)
    })

  const submitLogTimeOff = async (
    data: {
      startDate: string
      endDate: string
      description: string
      userId?: number
    },
    skipCalendarCheck = false,
  ): Promise<boolean> => {
    const targetUserId = data.userId ?? profile.id
    const targetUser =
      data.userId && data.userId !== profile.id
        ? allUsers.find(u => u.id === data.userId)
        : profile

    if (
      !skipCalendarCheck &&
      targetUser &&
      !targetUser.hasGoogleCalendarConnected
    ) {
      if (targetUserId === profile.id) {
        setPendingLogData(data)
        setIsGoogleModalOpen(true)
        return false
      }
      toast.error(
        `${targetUser.firstName} ${targetUser.lastName} has not connected Google Calendar. Time Off cannot be created.`,
        { toastId: 'google-calendar-not-connected' },
      )
      return false
    }

    try {
      await createTimeOff({
        userIdWhoCreatedTimeoff: profile.id,
        timeoffs: [
          {
            name: '',
            description: data.description,
            approved: false,
            userId: targetUserId,
            startDate: data.startDate,
            endDate: data.endDate,
          },
        ],
      }).unwrap()
      setShowLogModal(false)
      setPendingLogData(null)
      getUsersTimesOff(profile.id.toString())
      toast.success('Time Off created successfully', {
        toastId: 'create-timeoff-success',
      })
      return true
    } catch {
      toast.error('Something went wrong', { toastId: 'create-timeoff-error' })
      return false
    }
  }

  const submitWithGoogleLogin = async () => {
    if (!idToken || !pendingLogData) {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
      return
    }
    setIsConnectingGoogle(true)
    try {
      const oauthStart = await getGoogleCalendarOAuthStart(idToken).unwrap()
      const popup = window.open(
        oauthStart.authorizationUrl,
        'google-calendar-oauth',
        'width=600,height=700',
      )
      if (!popup) {
        toast.error(
          'Unable to open Google login window. Check popup permissions.',
          { toastId: 'google-popup-error' },
        )
        return
      }
      const result = await waitForGoogleOAuthResult(popup)
      if (result.success) {
        dispatch(timeoffApi.util.invalidateTags(['LoggedInUser']))
        toast.success('Google Calendar connected successfully.', {
          toastId: 'google-connected-success',
        })
        setIsGoogleModalOpen(false)
        await submitLogTimeOff(pendingLogData, true)
        return
      }
      toast.warning(
        result.message || 'Google login did not finish. Please try again.',
        { toastId: 'google-connected-warning' },
      )
    } catch {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  return (
    <>
      <LogTimeOffModal
        key={selectedTimeoff.id ?? 'edit-time-off'}
        show={isEditTimeoffModalOpen}
        onClose={() => setEditTimeoffModalOpen(false)}
        onSubmit={submitUpdateTimeOff}
        mode="edit"
        initialValues={
          selectedTimeoff.id
            ? {
                startDate: selectedTimeoff.startDate,
                endDate: selectedTimeoff.endDate,
                description: selectedTimeoff.description ?? '',
              }
            : undefined
        }
        onDelete={() => {
          setEditTimeoffModalOpen(false)
          setIsArchiveTimeOffModalOpen(true)
        }}
      />
      <DeleteTimeOffModal
        show={isArchiveTimeOffModalOpen}
        onClose={() => setIsArchiveTimeOffModalOpen(false)}
        onConfirm={() => {
          archiveTimeOff({ selectedTimeoff, archivedBy: profile })
          setIsArchiveTimeOffModalOpen(false)
        }}
      />
      {bambooModalOpen && (
        <ModalBambooIntegration
          isOpen={bambooModalOpen}
          onClose={() => setBambooModalOpen(false)}
        />
      )}
      <div className="team-timeoff">
        <h1 className="team-timeoff__title">Time Off Calendar</h1>
        <div className="team-timeoff__toolbar" aria-busy={isPreparingView}>
          <div className="team-timeoff__filters">
            <div className="team-timeoff__filter-field">
              <span className="team-timeoff__filter-label">Search</span>
              <div className="team-timeoff__search-wrap">
                <input
                  id="timeoffSearch"
                  className="team-timeoff__search-input"
                  placeholder="Type a Name"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
                <MagnifyingGlass
                  size={16}
                  weight="regular"
                  className="team-timeoff__search-icon"
                />
              </div>
            </div>
            <div className="team-timeoff__filter-field">
              <span className="team-timeoff__filter-label">Guild</span>
              <div className="team-timeoff__select-wrap" title={controlsTooltip}>
                <select
                  id="timeoffGuild"
                  className="team-timeoff__select"
                  value={selectedGuildId}
                  disabled={isPreparingView}
                  onChange={e => setSelectedGuildId(Number(e.target.value))}
                >
                  <option key="guild--1" value={-1}>
                    All Guilds
                  </option>
                  {activeGuilds.map(guild => (
                    <option key={`guild-${guild.id}`} value={guild.id}>
                      {guild.role}
                    </option>
                  ))}
                </select>
                <CaretDown
                  size={14}
                  weight="regular"
                  className="team-timeoff__select-icon"
                />
              </div>
            </div>
            <div className="team-timeoff__filter-field">
              <span className="team-timeoff__filter-label">Tribe</span>
              <div className="team-timeoff__select-wrap" title={controlsTooltip}>
                <select
                  id="timeoffTribe"
                  className="team-timeoff__select"
                  value={selectedTribe}
                  disabled={isPreparingView}
                  onChange={e => setSelectedTribe(parseInt(e.target.value))}
                >
                  <option value={0}>All Tribes</option>
                  {sortedTribes?.map(tribe => (
                    <option key={`tribe-${tribe.id}`} value={tribe.id}>
                      {tribe.name}
                    </option>
                  ))}
                </select>
                <CaretDown
                  size={14}
                  weight="regular"
                  className="team-timeoff__select-icon"
                />
              </div>
            </div>
            <div className="team-timeoff__filter-field">
              <span className="team-timeoff__filter-label">Team</span>
              <div className="team-timeoff__select-wrap" title={controlsTooltip}>
                <select
                  id="timeoffTeam"
                  className="team-timeoff__select"
                  value={selectedTeam}
                  disabled={isPreparingView}
                  onChange={e => setSelectedTeam(parseInt(e.target.value))}
                >
                  <option value={0}>All Teams</option>
                  {sortedTeams?.map(team => {
                    const today = new Date()
                    const currentEngagement = team.engagements?.find(
                      engagement => {
                        const startDate = new Date(engagement.startDate)
                        const endDate = new Date(engagement.endDate)
                        return startDate <= today && endDate >= today
                      },
                    )
                    return (
                      <option key={`team-${team.id}`} value={team.id}>
                        {team.name} -{' '}
                        {currentEngagement
                          ? currentEngagement.projectName
                          : 'Open'}
                      </option>
                    )
                  })}
                </select>
                <CaretDown
                  size={14}
                  weight="regular"
                  className="team-timeoff__select-icon"
                />
              </div>
            </div>
          </div>
          {profile.isAdmin && (
            <div className="team-timeoff__actions" title={controlsTooltip}>
              <button
                type="button"
                className="team-timeoff__bamboo-btn"
                disabled={isPreparingView}
                onClick={() => setBambooModalOpen(true)}
                title="Sync with BambooHR"
              >
                <img src={bamboohrLogo} alt="BambooHR" />
              </button>
              <button
                type="button"
                className="team-timeoff__add-btn"
                disabled={isPreparingView}
                onClick={handleAddTimeOff}
              >
                <Plus size={16} weight="regular" color="#ffffff" />
                Add New Time Off
              </button>
            </div>
          )}
        </div>

        <div className="team-timeoff__content">
          {showCalendar && (
            <div
              className={[
                'team-timeoff__calendar-wrap',
                isCalendarReady ? '' : 'team-timeoff__calendar-wrap--hidden',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <TeamTimeOffCalendar
                usersGroups={usersGroups}
                allTimesOff={allTimesOff}
                readOnly={!profile.isAdmin}
                isGroupNameClickable={profile.isAdmin}
                onGroupNameClick={
                  profile.isAdmin ? handleGroupNameClick : undefined
                }
                onItemSelect={selectItem}
              />
            </div>
          )}

          {!isLoading && !showCalendar && (
            <p className="team-timeoff__empty">No results found</p>
          )}

          {isPreparingView && (
            <div className="team-timeoff__content-loader">
              <Loader />
            </div>
          )}
        </div>
      </div>

      <LogTimeOffModal
        show={showLogModal}
        onClose={() => setShowLogModal(false)}
        onSubmit={submitLogTimeOff}
        users={profile.isAdmin ? allUsers : undefined}
      />

      <GoogleCalendarConsentModal
        show={isGoogleModalOpen}
        isConnecting={isConnectingGoogle}
        onClose={() => setIsGoogleModalOpen(false)}
        onConnect={submitWithGoogleLogin}
      />
    </>
  )
}

export default TeamTimeOffView
